import { validateFrequencyFilters } from "modules/came/model/frequencyModel";
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { FilterAltOutlined, RestartAlt } from "@mui/icons-material";

const FiltrosFrecuencia = ({
  filters,
  ticketTypeOptions,
  onFilterChange,
  onReset,
  onRefresh,
  loading,
  reportName = "frecuencia",
}) => {
  const { startDate, endDate, ticketTypes } = filters;
  const rangeError = validateFrequencyFilters(filters);

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    onFilterChange({ startDate: value });
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    onFilterChange({ endDate: value });
  };

  const handleTicketTypeChange = (event, newValue) => {
    onFilterChange({ ticketTypes: newValue });
  };

  // Función estable para aplicar filtros
  const handleApplyFilters = React.useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const fieldStyles = {
    "& .MuiOutlinedInput-root": { minHeight: 46, borderRadius: 2, bgcolor: "background.paper" },
    "& .MuiInputBase-input": { fontSize: "0.875rem" },
    "& .MuiFormHelperText-root": { mx: 0, mt: 0.75, lineHeight: 1.5 },
  };

  return (
    <Box component="section" aria-label={`Filtros del informe de ${reportName}`}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
        <FilterAltOutlined sx={{ color: "info.main", fontSize: 21 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Filtros de consulta
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "minmax(170px, 1fr) minmax(170px, 1fr) minmax(240px, 1.5fr) auto",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <TextField
          type="date"
          label="Fecha de inicio"
          inputProps={{ max: endDate || undefined }}
          helperText="Desde las 00:00"
          value={startDate || ""}
          onChange={handleStartDateChange}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={fieldStyles}
        />
        <TextField
          type="date"
          label="Fecha de fin"
          inputProps={{ min: startDate || undefined }}
          error={Boolean(rangeError)}
          helperText={rangeError || "Hasta las 23:59"}
          value={endDate || ""}
          onChange={handleEndDateChange}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={fieldStyles}
        />
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={2}
          options={ticketTypeOptions}
          getOptionLabel={(option) => option.label}
          value={ticketTypes}
          onChange={handleTicketTypeChange}
          noOptionsText="No hay tipos disponibles"
          openText="Mostrar tipos"
          closeText="Cerrar tipos"
          clearText="Limpiar selección"
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Tipos de tarjeta"
              InputLabelProps={{ shrink: true }}
              placeholder={ticketTypes.length ? "" : "Todos los tipos"}
              helperText="Puedes seleccionar varios tipos de tarjeta"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.value}
                label={option.label}
                {...getTagProps({ index })}
                size="small"
                sx={{
                  bgcolor: "#e8f1fb",
                  color: "#17466e",
                  maxWidth: 190,
                  "& .MuiChip-deleteIcon": { color: "#446b90" },
                }}
              />
            ))
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          sx={{ ...fieldStyles, minWidth: 0, "& .MuiAutocomplete-inputRoot": { py: 0.5 } }}
        />
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            "& .MuiButton-root": {
              minHeight: 46,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.8125rem",
              px: 2,
              flex: { xs: 1, lg: "initial" },
            },
          }}
        >
          <Button
            variant="contained"
            color="info"
            onClick={handleApplyFilters}
            disabled={loading || Boolean(rangeError)}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <FilterAltOutlined />
            }
            sx={{
              bgcolor: "info.main",
              color: "#fff",
              "&:hover": { bgcolor: "info.dark", color: "#fff" },
              "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "text.disabled" },
            }}
          >
            {loading ? "Consultando…" : "Aplicar"}
          </Button>
          <Button
            variant="outlined"
            onClick={onReset}
            disabled={loading}
            startIcon={<RestartAlt />}
            sx={{
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": { borderColor: "text.secondary" },
            }}
          >
            Restablecer
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

FiltrosFrecuencia.propTypes = {
  reportName: PropTypes.string,
  filters: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    ticketTypes: PropTypes.array,
  }).isRequired,
  ticketTypeOptions: PropTypes.array.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default FiltrosFrecuencia;
